import React from "react";
import styled from "styled-components";
import { useSearchParams } from "react-router-dom";
import { Flex } from "../../../components/Flex";
import { Spacing } from "../../../theme/Spacing";
import { Controller, useFormContext } from "react-hook-form";
import { GetReportsDto } from "../../../model/reports/dto/GetReportsDto";
import { AtiraSelect } from "../../../components/AtiraSelect";
import { Range } from "../../../model/shared/dto/GenericWithRange";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import { reportActions } from "../../../redux/report/report.slice";
import { userSliceSelectors } from "../../../redux/user/user.selector";
import isNil from "lodash/isNil";
import omitBy from "lodash/omitBy";
import { reportSliceSelectors } from "../../../redux/report/report.selector";
import { rangeOptions } from "../../../utils/Date";
import { AtiraDateRange } from "../../../components/AtiraDateRange";
import { ReportExportButton } from "./ReportExportButton";
import { AtiraToast } from "../../../utils/AtiraToast";
import { sub } from "date-fns";
import dayjs from "dayjs";

const FiltersWrapper = styled(Flex)`
  align-items: center;
  gap: ${Spacing.m};
  flex-wrap: wrap;
  min-height: 3rem;
`;

export const ReportFilters: React.FC = () => {
  const { t } = useTranslation();
  const { control, getValues, setValue } = useFormContext<GetReportsDto>();
  const dispatch = useAppDispatch();
  const [, setSearchParams] = useSearchParams();

  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;
  const reportsLoading = useAppSelector(
    reportSliceSelectors.selectReportLoading,
  );

  const _rangeOptions = rangeOptions(t).filter(
    (option) => option.value !== null,
  );

  const onFilter = async () => {
    try {
      await dispatch(
        reportActions.getReport({
          ...(omitBy(getValues(), isNil) as GetReportsDto),
          userId,
        }),
      ).unwrap();
    } catch (e) {
      // A failed report and an empty one look identical otherwise.
      AtiraToast.apiError(e);
    }
  };

  return (
    <FiltersWrapper>
      <Controller
        control={control}
        name="range"
        render={({ field: { value, onChange } }) => (
          <AtiraSelect
            options={_rangeOptions}
            // The select takes a value, not an option object: passing the
            // option made the control render "[object Object]" once a custom
            // date range had cleared the preset.
            value={value ?? undefined}
            placeholder={t("reports.filters.range.placeholder")}
            // Not clearable: a report is always of some window, and clearing
            // this sent neither range nor scope, which the API rejects.
            onChange={(v) => {
              onChange(v);
              setValue("scope", undefined);
              onFilter();
              setSearchParams(
                (prev) => {
                  const next = new URLSearchParams(prev);
                  next.set("range", v as string);
                  next.delete("scope");
                  return next;
                },
                { replace: true },
              );
            }}
            containerStyle={{ width: "12rem" }}
            disabled={reportsLoading}
          />
        )}
      />

      <Controller
        control={control}
        name="scope"
        render={({ field: { value, onChange } }) => (
          <AtiraDateRange
            value={value}
            onChange={(dateStrings) => {
              const scope = dateStrings?.filter(Boolean).length
                ? (dateStrings as [string, string])
                : undefined;

              // The two controls are alternatives, so picking one has to clear
              // the other on screen as well as in the payload.
              onChange(scope);
              setValue("range", scope ? undefined : Range.THIS_WEEK);
              onFilter();
              setSearchParams(
                (prev) => {
                  const next = new URLSearchParams(prev);
                  if (scope) {
                    next.set("scope", scope.join(","));
                    next.delete("range");
                  } else {
                    next.set("range", Range.THIS_WEEK);
                    next.delete("scope");
                  }
                  return next;
                },
                { replace: true },
              );
            }}
            loading={reportsLoading}
            minDate={dayjs(sub(new Date(), { months: 3 }))}
            maxDate={dayjs()}
          />
        )}
      />

      <ReportExportButton />
    </FiltersWrapper>
  );
};
